<template>
    <Card :title="$t('labels.order')">
        <div class="card-body">
            <div class="row">
                <div class="col-lg-2 col-md-4 col-sm-12">
                    <FormGroup :label="$t('labels.status')">
                        <AutoComplete 
                            :url="`/projects/${$route.params.projectUuid}/crm/statuses`"
                            :model-value="order?.status?.uuid"
                            :selected-value="order?.status?.name"
                            :can-be-empty="true"
                            :hasCreateButton="true"
                            :limit="30"
                            @click:create="$refs.CreateOrderStatusModal.show()"
                            @update:model-value="order.status = $event"
                            :query-params="{
                                orders: [
                                    {
                                        column: 'priority_order',
                                        dir: 'asc'
                                    }
                                ]
                            }"
                        >
                            <template #optionContent v-if="order?.status">
                                <span class="badge" :style="{
                                    background: order?.status?.color,
                                    color: 'white'
                                }">{{ order?.status?.name }}</span>
                            </template>
                            <template #optionItem="{option, index}">
                                <span class="badge" :style="{
                                    background: option.color,
                                    color: 'white'
                                }">{{ option.name }}</span>
                            </template>
                        </AutoComplete>
                    </FormGroup>
                </div>
                <div class="col-lg-2 col-md-4 col-sm-12">
                    <FormGroup :label="$t('labels.order_source')">
                        <AutoComplete
                            :url="`/projects/${$route.params.projectUuid}/crm/sources`"
                            :model-value="order?.source?.uuid"
                            :selected-value="order?.source?.name"
                            :can-be-empty="true"
                            :query-params="{
                                extra_fields: ['imageFile']
                            }"
                            @update:model-value="order.source = $event"
                        >
                            <template #optionContent>
                                <div class="d-flex">
                                    <img class="image-preview mr-1" :src="order?.source?.image_file?.url || PLACEHOLDER_IMAGE_URL" />
                                    <span class="source-name">{{ order?.source?.name || $t('labels.empty') }}</span>
                                </div>
                            </template>
                            <template #optionItem="{option, index}">
                                <div class="d-flex">
                                    <img class="image-preview mr-1" :src="option?.image_file?.url || PLACEHOLDER_IMAGE_URL" />
                                    {{ option.name }}
                                </div>
                            </template>
                        </AutoComplete>
                    </FormGroup>
                </div>
                <div class="col-lg-2 col-md-4 col-sm-12">
                    <FormGroup :label="$t('labels.external_id')">
                        <Input
                            :model-value="order?.external_order_number"
                            @update:model-value="order.external_order_number = $event"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-2 col-md-4 col-sm-12">
                    <FormGroup :label="$t('table.titles.payment')">
                        <div class="static-value d-flex align-items-center">
                            <template v-if="payment">
                                <span class="text-truncate mr-2" :title="$t(`enums.OrderPaymentTypeEnum.${payment.type}`)">{{ $t(`enums.OrderPaymentTypeEnum.${payment.type}`) }}</span>
                                <span :class="['badge badge-pill flex-shrink-0', payment.is_paid ? 'badge-soft-success' : 'badge-soft-danger']">
                                    {{ payment.is_paid ? $t('labels.paid') : $t('labels.not_paid') }}
                                </span>
                            </template>
                            <u class="text-muted" v-else>{{ $t('labels.empty') }}</u>
                        </div>
                    </FormGroup>
                </div>
                <div class="col-lg-2 col-md-4 col-sm-12">
                    <FormGroup :label="$t('table.titles.delivery')">
                        <div class="static-value d-flex align-items-center">
                            <OrderDeliveryCell
                                v-if="delivery"
                                :delivery="delivery"
                                :order="order"
                                @created="delivery = $event"
                            />
                            <u class="text-muted" v-else>{{ $t('labels.empty') }}</u>
                        </div>
                    </FormGroup>
                </div>
                <div class="col-lg-2 col-md-8 col-sm-12">
                    <FormGroup :label="$t('labels.client_comment')" class="mb-0">
                        <Input
                            :model-value="order?.comment"
                            @update:model-value="order.comment = $event"
                        />
                    </FormGroup>
                </div>
            </div>
        </div>
        <div class="card-footer d-flex justify-content-between align-items-center">
            <div class="list-icons text-muted">
                <span class="list-icons-item">
                    <i class="icon-plus3 mr-2"></i> {{ DateFormatter.formatDateTime(order?.created_at) }}
                </span>

                <span class="list-icons-item ml-3">
                    <i class="icon-pencil mr-2"></i> {{ DateFormatter.formatDateTime(order?.updated_at) }}
                </span>

                <span class="list-icons-item ml-3" v-if="order?.closed_at">
                    <i class="icon-checkmark mr-2"></i> {{ DateFormatter.formatDateTime(order?.closed_at) }}
                </span>
            </div>
            <div>
                <Button color="light" class="mr-2" @click="remove">
                    <i class="icon-trash mr-2"></i>
                    <span>{{ $t('buttons.delete') }}</span>
                </Button>
                <Button color="indigo" @click="save">
                    <i class="icon-floppy-disk mr-2"></i>
                    <span>{{ $t('buttons.edit') }}</span>
                </Button>
            </div>
        </div>
    </Card>
    <CreateOrderStatusModal ref="CreateOrderStatusModal" @status:created="status = $event"/>
    <CreateOrderClientModal ref="CreateOrderClientModal" @client:created="client = $event"/>
</template>
<script>
import Card from "../../../../../components/new/CardComponent.vue";
import FormGroup from "../../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../../components/new/form/Input.vue";
import AutoComplete from "../../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import Button from "../../../../../components/new/button/AppButton.vue";
import CreateOrderStatusModal from "../modals/CreateOrderStatusModal.vue";
import CreateOrderClientModal from "../modals/CreateOrderClientModal.vue";
import OrderDeliveryCell from "../index/OrderDeliveryCell.vue";

import { privateChannel } from "../../../../services/echoService";
import { mapGetters } from "vuex";

import { httpClient } from "../../../../../services/HttpClient";
import MessageNotificator from "../../../../../services/MessageNotificator";
import { PLACEHOLDER_IMAGE_URL } from "../../../../constants";
import DateFormatter from "../../../../../services/DateFormatter";

const STATUS_UPDATED_EVENT = '\\App\\Events\\Crm\\Delivery\\DeliveryStatusUpdatedEvent';

export default {
    name: "OrderMainInformation",
    components: {
        Card, FormGroup, Input, AutoComplete, Button, CreateOrderStatusModal, CreateOrderClientModal,
        OrderDeliveryCell
    },
    props: {
        order: {
            type: [Object, null],
            default: null
        }
    },
    emits: ['update'],
    data() {
        return {
            DateFormatter,
            PLACEHOLDER_IMAGE_URL,
            payment: null,
            delivery: null
        }
    },
    computed: {
        ...mapGetters({
            project: "route/project"
        })
    },
    watch: {
        order: {
            immediate: true,
            handler(order) {
                if (!order) {
                    return
                }

                this.fetchPayment()
                this.fetchDelivery()
            }
        }
    },
    methods: {
        async fetchPayment() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.order.uuid}/payments`, {
                limit: 1,
                orders: [{
                    column: 'id',
                    dir: 'asc'
                }]
            })

            this.payment = response.data.data[0] ?? null
        },
        async fetchDelivery() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.order.uuid}/deliveries`, {
                limit: 1,
                orders: [{
                    column: 'id',
                    dir: 'asc'
                }]
            })

            this.delivery = response.data.data[0] ?? null
        },
        subscribeToDeliveryEvents() {
            this.channel = privateChannel(`projects.${this.project.id}`)
                .listen(STATUS_UPDATED_EVENT, this.onDeliveryStatusUpdated);
        },
        unsubscribeFromDeliveryEvents() {
            if (!this.channel) {
                return;
            }

            this.channel.stopListening(STATUS_UPDATED_EVENT, this.onDeliveryStatusUpdated);

            this.channel = null;
        },
        onDeliveryStatusUpdated(event) {
            if (this.delivery?.uuid !== event.delivery_uuid) {
                return;
            }

            this.delivery.status_label = event.status?.name || '';
            this.delivery.is_updating_status = false;
        },
        async save() {
            await httpClient.patch(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.$route.params.orderUuid}`, {
                status_uuid: this.order.status ? this.order.status.uuid : null,
                comment: this.order.comment,
                source_uuid: this.order.source ? this.order.source.uuid : null,
                external_order_number: this.order.external_order_number
            }).then((response) => {
                new MessageNotificator().success(this.$t('alerts.success'))
                this.$emit("update", response.data.data)
            })
        },
        remove() {
            new MessageNotificator().confirm(this.$t('alerts.delete_crm_order'), () => {
                httpClient.delete(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.order.uuid}`).then(() => {
                    this.$router.replace({ name: 'OrdersTablePage' })
                })
            })
        }
    },
    mounted() {
        this.subscribeToDeliveryEvents();
    },
    unmounted() {
        this.unsubscribeFromDeliveryEvents();
    }
}
</script>
<style scoped>
.image-preview {
    border-radius: 5px;
    object-fit: cover;
    object-position: center;
    width: 20px;
    height: 20px;
}
.static-value {
    min-height: calc(1.5385em + 1rem + 2px);
    overflow: hidden;
}
</style>